import NProgress from "nprogress";
import {
  FULL_SCREEN_DELAY_MS,
  LOADING_TYPES,
  MIN_VISIBLE_MS,
  NPROGRESS_TRICKLE_MS,
} from "./constants";
import {
  formatProgressLabel,
  formatProgressPercent,
  resolveProgressMode,
  PROGRESS_MODE,
} from "./progressMode";

let taskIdSeq = 0;

function nextId() {
  taskIdSeq += 1;
  return `load-${taskIdSeq}-${Date.now()}`;
}

NProgress.configure({
  showSpinner: false,
  minimum: 0,
  trickle: false,
  trickleSpeed: NPROGRESS_TRICKLE_MS,
  easing: "ease",
  speed: 350,
});

/**
 * @typedef {{
 *   id: string,
 *   message?: string,
 *   type?: string,
 *   overlay?: boolean,
 *   report?: boolean,
 *   startedAt: number,
 *   mode: string,
 *   current: number,
 *   total: number,
 *   percent: number|null,
 *   progressLabel: string,
 * }} LoadingTask
 */

class LoadingManager {
  constructor() {
    /** @type {Map<string, LoadingTask>} */
    this.tasks = new Map();
    this.firstStartAt = 0;
    this.hideTimer = null;
    this.fullScreenTimer = null;
    this.visible = false;
    this.fullScreen = false;
    this.overlay = false;
    this.error = null;
    this.messageIndex = 0;
    /** @type {Set<(state: object) => void>} */
    this.listeners = new Set();
  }

  get count() {
    return this.tasks.size;
  }

  get isLoading() {
    return this.count > 0;
  }

  get primaryTask() {
    const tasks = [...this.tasks.values()];
    const overlayTask = tasks.find((t) => t.overlay);
    if (overlayTask) return overlayTask;
    const determinate = tasks.find((t) => t.mode === PROGRESS_MODE.DETERMINATE && t.total > 0);
    if (determinate) return determinate;
    const reportTask = tasks.find((t) => t.report || t.type === LOADING_TYPES.REPORT);
    if (reportTask) return reportTask;
    return tasks[tasks.length - 1] || null;
  }

  getProgressSnapshot() {
    const primary = this.primaryTask;
    if (!primary) {
      return {
        progressMode: PROGRESS_MODE.INDETERMINATE,
        progressPercent: null,
        progressLabel: "",
        progressCurrent: 0,
        progressTotal: 0,
      };
    }

    if (primary.mode === PROGRESS_MODE.DETERMINATE && primary.total > 0) {
      return {
        progressMode: PROGRESS_MODE.DETERMINATE,
        progressPercent: primary.percent,
        progressLabel: primary.progressLabel,
        progressCurrent: primary.current,
        progressTotal: primary.total,
      };
    }

    return {
      progressMode: PROGRESS_MODE.INDETERMINATE,
      progressPercent: null,
      progressLabel: primary.progressLabel || "",
      progressCurrent: 0,
      progressTotal: 0,
    };
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  getState() {
    const primary = this.primaryTask;
    const progress = this.getProgressSnapshot();
    return {
      count: this.count,
      isLoading: this.isLoading,
      visible: this.visible,
      fullScreen: this.fullScreen,
      overlay: this.overlay,
      error: this.error,
      message: primary?.message || "",
      taskType: primary?.type || LOADING_TYPES.DEFAULT,
      messageIndex: this.messageIndex,
      ...progress,
    };
  }

  notify() {
    this.syncTopBar();
    const state = this.getState();
    for (const listener of this.listeners) listener(state);
  }

  syncTopBar() {
    const { progressMode, progressPercent } = this.getProgressSnapshot();
    const root = document.documentElement;
    root.classList.toggle("gl-loading-active", this.visible);
    root.classList.toggle("gl-progress-indeterminate", this.visible && progressMode === PROGRESS_MODE.INDETERMINATE);
    root.classList.toggle("gl-progress-determinate", this.visible && progressMode === PROGRESS_MODE.DETERMINATE);

    if (!this.visible) {
      NProgress.done();
      return;
    }

    if (progressMode === PROGRESS_MODE.DETERMINATE && progressPercent != null) {
      if (!NProgress.isStarted()) NProgress.start();
      NProgress.set(progressPercent / 100);
    } else if (!NProgress.isStarted()) {
      NProgress.start();
      NProgress.set(0);
    }
  }

  clearHideTimer() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  clearFullScreenTimer() {
    if (this.fullScreenTimer) {
      clearTimeout(this.fullScreenTimer);
      this.fullScreenTimer = null;
    }
  }

  showUi() {
    if (!this.visible) {
      this.visible = true;
      if (!NProgress.isStarted()) NProgress.start();
    }
    this.clearHideTimer();
    this.syncTopBar();
  }

  scheduleHide() {
    this.clearHideTimer();
    const elapsed = Date.now() - this.firstStartAt;
    const delay = Math.max(0, MIN_VISIBLE_MS - elapsed);
    this.hideTimer = setTimeout(() => {
      if (this.tasks.size === 0) {
        this.visible = false;
        this.fullScreen = false;
        this.overlay = false;
        NProgress.done();
        document.documentElement.classList.remove(
          "gl-loading-active",
          "gl-progress-indeterminate",
          "gl-progress-determinate"
        );
        this.clearFullScreenTimer();
        this.notify();
      }
    }, delay);
  }

  updateModes() {
    const tasks = [...this.tasks.values()];
    this.overlay = tasks.some((t) => t.overlay);

    this.clearFullScreenTimer();
    if (this.tasks.size > 0) {
      this.fullScreenTimer = setTimeout(() => {
        if (this.tasks.size > 0) {
          this.fullScreen = true;
          this.notify();
        }
      }, FULL_SCREEN_DELAY_MS);
    } else {
      this.fullScreen = false;
    }
  }

  /**
   * @param {{
   *   message?: string,
   *   type?: string,
   *   mode?: string,
   *   overlay?: boolean,
   *   report?: boolean,
   *   id?: string,
   *   total?: number,
   *   current?: number,
   *   progressLabel?: string,
   *   determinate?: boolean,
   *   indeterminate?: boolean,
   * }} [options]
   * @returns {string} task id
   */
  startLoading(options = {}) {
    const id = options.id || nextId();
    if (this.tasks.has(id)) return id;

    const mode = resolveProgressMode(options);
    const total = Number(options.total) || 0;
    const current = Number(options.current) || 0;
    const percent = mode === PROGRESS_MODE.DETERMINATE ? formatProgressPercent(current, total) : null;

    const task = {
      id,
      message: options.message || "",
      type: options.type || LOADING_TYPES.DEFAULT,
      overlay: Boolean(options.overlay),
      report: Boolean(options.report),
      startedAt: Date.now(),
      mode,
      current,
      total,
      percent,
      progressLabel:
        options.progressLabel ||
        (mode === PROGRESS_MODE.DETERMINATE && total > 0 ? formatProgressLabel(current, total) : ""),
    };

    if (this.tasks.size === 0) {
      this.firstStartAt = Date.now();
      this.error = null;
    }

    this.tasks.set(id, task);
    this.showUi();
    this.updateModes();
    this.notify();
    return id;
  }

  stopLoading(id) {
    if (id) {
      this.tasks.delete(id);
    } else {
      const last = [...this.tasks.keys()].pop();
      if (last) this.tasks.delete(last);
    }

    if (this.tasks.size === 0) {
      this.scheduleHide();
    } else {
      this.updateModes();
      this.syncTopBar();
    }
    this.notify();
  }

  /**
   * Update determinate progress for a task.
   * @param {string} id
   * @param {number} current
   * @param {number} total
   * @param {string} [message]
   * @param {string} [unit] e.g. "months", "rows"
   */
  setTaskProgress(id, current, total, message, unit) {
    const task = this.tasks.get(id);
    if (!task) return;

    task.mode = PROGRESS_MODE.DETERMINATE;
    task.current = Math.max(0, Number(current) || 0);
    task.total = Math.max(0, Number(total) || 0);
    task.percent = formatProgressPercent(task.current, task.total);
    task.progressLabel = formatProgressLabel(task.current, task.total, unit || "");
    if (message) task.message = message;

    this.syncTopBar();
    this.notify();
  }

  /** @deprecated use setTaskProgress — sets fraction on primary determinate task */
  setProgress(value) {
    if (typeof value !== "number" || value < 0 || value > 1) return;
    const primary = this.primaryTask;
    if (!primary) return;
    primary.mode = PROGRESS_MODE.DETERMINATE;
    primary.percent = Math.min(100, Math.round(value * 100));
    primary.current = primary.percent;
    primary.total = 100;
    primary.progressLabel = `${primary.percent}%`;
    this.syncTopBar();
    this.notify();
  }

  setTaskMessage(id, message) {
    const task = this.tasks.get(id);
    if (task && message) {
      task.message = message;
      this.notify();
    }
  }

  setMessage(id, message) {
    this.setTaskMessage(id, message);
  }

  rotateMessage() {
    const primary = this.primaryTask;
    if (primary?.mode === PROGRESS_MODE.DETERMINATE) return;
    this.messageIndex += 1;
    this.notify();
  }

  setError(error) {
    this.error = error;
    this.tasks.clear();
    this.clearHideTimer();
    this.clearFullScreenTimer();
    this.visible = false;
    this.fullScreen = false;
    this.overlay = false;
    NProgress.done();
    document.documentElement.classList.remove(
      "gl-loading-active",
      "gl-progress-indeterminate",
      "gl-progress-determinate"
    );
    this.notify();
  }

  clearError() {
    this.error = null;
    this.notify();
  }

  async withLoading(fn, options = {}) {
    const id = this.startLoading(options);
    try {
      const result = await fn(id);
      if (options.total) {
        this.setTaskProgress(id, options.total, options.total);
      }
      return result;
    } catch (err) {
      this.setError(err?.message || "Something went wrong");
      throw err;
    } finally {
      this.stopLoading(id);
    }
  }
}

export const loadingManager = new LoadingManager();

export function startLoading(options) {
  return loadingManager.startLoading(options);
}

export function stopLoading(id) {
  loadingManager.stopLoading(id);
}

export function setTaskProgress(id, current, total, message, unit) {
  loadingManager.setTaskProgress(id, current, total, message, unit);
}

export function setTaskMessage(id, message) {
  loadingManager.setTaskMessage(id, message);
}

export function setProgress(value) {
  loadingManager.setProgress(value);
}

export function withLoading(fn, options) {
  return loadingManager.withLoading(fn, options);
}
